import { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import isToken from "../helpers/token-check";
import { isFamilyOffice, isMembershipActive } from "../helpers/membership";
import { UPGRADE_CHECKOUT_PRODUCTS } from "../features/UpgradeCheckout/productConfig";

/** Check if user has Centurion or Mars Mastermind from /me (granted directly, not via purchase). */
const hasAccessFromUserFlags = (normalizedInput, user) => {
  if (!user) return false;
  return (
    (normalizedInput.includes("centurion") && user.IsCenturion) ||
    (normalizedInput.includes("the-mars-mastermind") && user.IsMars) ||
    (normalizedInput.includes("premium") &&
      user.StocksMembershipTillText !== "Free User") ||
    (normalizedInput.includes("spikingpremium") &&
      user.StocksMembershipTillText !== "Free User")
  );
};

/**
 * @param {string[]} productIds - List of product IDs that grant access
 * @returns {{ accessPermission: boolean, isLoading: boolean }}
 * Uses home.purchasedProductIds from Redux (filled after getUserDetails); no per-mount API calls.
 */
export const usePurchasedPermission = (productIds) => {
  const user = useSelector((state) => state.home?.user);
  const purchasedProductIds = useSelector(
    (state) => state.home?.purchasedProductIds || []
  );
  const purchasedProductIdsLoaded = useSelector(
    (state) => state.home?.purchasedProductIdsLoaded === true
  );

  const productIdsKey = productIds?.length
    ? productIds.map((id) => (id || "").toString().toLowerCase()).join(",")
    : "";

  const { accessPermission, isLoading } = useMemo(() => {
    if (!productIdsKey) {
      return { accessPermission: false, isLoading: false };
    }
    if (!isToken()) {
      return { accessPermission: false, isLoading: false };
    }
    const normalizedInput = productIdsKey.split(",").filter(Boolean);
    if (hasAccessFromUserFlags(normalizedInput, user)) {
      return { accessPermission: true, isLoading: false };
    }
    if (!purchasedProductIdsLoaded) {
      return { accessPermission: false, isLoading: true };
    }
    const idSet = new Set(
      purchasedProductIds.map((id) => (id || "").toString().toLowerCase())
    );

    const hasAccessFromPurchases = normalizedInput.some((id) => idSet.has(id));
    return { accessPermission: hasAccessFromPurchases, isLoading: false };
  }, [productIdsKey, user, purchasedProductIds, purchasedProductIdsLoaded]);

  return { accessPermission, isLoading };
};

/**
 * Single source of truth for product access. Reads home.productAccess from Redux
 * (filled from GET /v3/my/product-access after getUserDetails); no per-mount API calls.
 * Tier grants are already resolved into products server-side, so this is a plain lookup.
 * @param {string} productAlias - canonical product alias (see src/constant/products.js)
 * @returns {{ accessPermission: boolean, isLoading: boolean }}
 */
export const useProductAccess = (productAlias) => {
  const productAccess = useSelector((state) => state.home?.productAccess || []);
  const productAccessLoaded = useSelector(
    (state) => state.home?.productAccessLoaded === true
  );
  const normalizedAlias = (productAlias || "").toString().toLowerCase();

  return useMemo(() => {
    if (!normalizedAlias || !isToken()) {
      return { accessPermission: false, isLoading: false };
    }
    if (!productAccessLoaded) {
      return { accessPermission: false, isLoading: true };
    }
    const now = Date.now();
    const accessPermission = productAccess.some((entry) => {
      if ((entry.product || "").toString().toLowerCase() !== normalizedAlias) {
        return false;
      }
      if (!entry.expires_at) return true; // no expiry = lifetime
      const expiresAt = new Date(entry.expires_at).getTime();
      return Number.isNaN(expiresAt) || expiresAt > now;
    });
    return { accessPermission, isLoading: false };
  }, [normalizedAlias, productAccess, productAccessLoaded]);
};

/**
 * Whether the current user holds a non-expired membership tier (any paid tier).
 * Backed by the /v3/me MembershipTier / MembershipExpiresAt fields.
 * @returns {{ isActive: boolean, isLoading: boolean }}
 */
export const useMembershipActive = () => {
  const user = useSelector((state) => state.home?.user);
  const userDetailsLoading = useSelector(
    (state) => state.home?.userDetailsLoading === true
  );

  return useMemo(() => {
    if (!isToken()) {
      return { isActive: false, isLoading: false };
    }
    if (!user) {
      return { isActive: false, isLoading: userDetailsLoading };
    }
    return { isActive: isMembershipActive(user), isLoading: false };
  }, [user, userDetailsLoading]);
};

/**
 * Whether the current user holds a non-expired Family Office membership.
 * Backed by the /v3/me MembershipTier / MembershipExpiresAt fields.
 * @returns {{ hasAccess: boolean, isLoading: boolean }}
 */
export const useFamilyOfficeAccess = () => {
  const user = useSelector((state) => state.home?.user);
  const userDetailsLoading = useSelector(
    (state) => state.home?.userDetailsLoading === true
  );

  return useMemo(() => {
    if (!isToken()) {
      return { hasAccess: false, isLoading: false };
    }
    if (!user) {
      return { hasAccess: false, isLoading: userDetailsLoading };
    }
    return { hasAccess: isFamilyOffice(user), isLoading: false };
  }, [user, userDetailsLoading]);
};

/**
 * Fetches v3/my/past-transactions to check purchase history (from Redux cache).
 */
export const useHasPurchasedProduct = (productAlias) => {
  const [hasPurchased, setHasPurchased] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const purchasedProductIds = useSelector(
    (state) => state.home?.purchasedProductIds || []
  );
  const purchasedProductIdsLoaded = useSelector(
    (state) => state.home?.purchasedProductIdsLoaded === true
  );

  useEffect(() => {
    if (!productAlias || !isToken()) {
      setHasPurchased(false);
      setIsLoading(false);
      return;
    }
    if (!purchasedProductIdsLoaded) {
      setIsLoading(true);
      return;
    }
    const product =
      UPGRADE_CHECKOUT_PRODUCTS[(productAlias || "").toLowerCase()];
    if (!product) {
      setHasPurchased(false);
      setIsLoading(false);
      return;
    }
    const productIdsToCheck = new Set(
      [
        (product.monthlyProductId || "").toLowerCase(),
        (product.yearlyProductId || "").toLowerCase(),
      ].filter(Boolean)
    );
    const idSet = new Set(
      purchasedProductIds.map((id) => (id || "").toString().toLowerCase())
    );
    const purchased = [...productIdsToCheck].some((id) => idSet.has(id));
    setHasPurchased(purchased);
    setIsLoading(false);
  }, [productAlias, purchasedProductIds, purchasedProductIdsLoaded]);

  return { hasPurchased, isLoading };
};
